import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { Role } from "@common/ADAPT.Common.Model/organisation/role";
import { RoleLocation } from "@common/ADAPT.Common.Model/organisation/role-location";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { ArrayUtilities } from "@common/lib/utilities/array-utilities";
import { SortUtilities } from "@common/lib/utilities/sort-utilities";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { IDxListItemDeletedEvent } from "@common/ux/dx.types";
import { CommonIntegratedArchitectureFrameworkService } from "@org-common/lib/architecture/common-integrated-architecture-framework.service";
import { SelectTeamComponent } from "@org-common/lib/teams/team-dashboard-shared/select-team/select-team.component";
import { IntegratedArchitectureFrameworkAuthService } from "app/features/architecture/integrated-architecture/integrated-architecture-framework-auth.service";
import { lastValueFrom } from "rxjs";

@Component({
    selector: "adapt-edit-role-locations",
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: "./edit-role-locations.component.html",
})
export class EditRoleLocationsComponent extends BaseComponent implements OnInit {
    @Input({ required: true }) public role!: Role;
    @Input() public readonly = false;
    @Output() public entitiesChange = new EventEmitter<RoleLocation[]>();

    public roleLocations: RoleLocation[] = [];

    public configurableTeams: Team[] = [];

    public selectedTeam?: Team;
    @ViewChild(SelectTeamComponent) private selectTeamComponent?: SelectTeamComponent;

    private changedEntities: RoleLocation[] = [];

    public constructor(
        private commonDataService: CommonDataService,
        private commonIntegratedArchitectureService: CommonIntegratedArchitectureFrameworkService,
        private archAuthService: IntegratedArchitectureFrameworkAuthService,
    ) {
        super();
    }

    public async ngOnInit() {
        await this.updateData();
    }

    private async updateData() {
        this.configurableTeams = await lastValueFrom(this.archAuthService.getConfigurableTeams());
        this.roleLocations = (await lastValueFrom(this.commonIntegratedArchitectureService.getTeamRoleLocationsForRole(this.role.roleId)))
            .sort(SortUtilities.getSortByFieldFunction<RoleLocation>("team.teamTypeConfiguration.ordinal", "team.name"));

        setTimeout(() => {
            this.selectedTeam = undefined;
            this.selectTeamComponent?.reload();
        });
    }

    public async onTeamSelected(team?: Team) {
        if (!team) {
            return;
        }

        const roleLocation = await lastValueFrom(this.commonIntegratedArchitectureService.createRoleLocationForTeam(this.role, team));
        ArrayUtilities.addElementIfNotAlreadyExists(this.changedEntities, roleLocation);
        this.entitiesChange.emit(this.changedEntities);

        await this.updateData();
    }

    public async deleteRoleLocation(e: IDxListItemDeletedEvent<RoleLocation>) {
        const roleLocation = e.itemData;
        if (!roleLocation) {
            return;
        }

        await lastValueFrom(this.commonDataService.remove(roleLocation));

        ArrayUtilities.addElementIfNotAlreadyExists(this.changedEntities, roleLocation);
        this.entitiesChange.emit(this.changedEntities);

        await this.updateData();
    }

    @Autobind
    public teamFilter(team: Team) {
        return !this.roleLocations.find((l) => l.teamId === team.teamId)
            && this.configurableTeams.includes(team);
    }
}
